import React, { useEffect, useState } from 'react'
import { Spin, Dropdown, message as AntMessage, Tooltip } from 'antd'

import styles from './style.module.scss'
import menuCheckGray16 from 'assets/images/menu-check-gray-16.svg'
import buttonMenu from 'assets/images/button-menu-h.svg'

import mailboxApiHelper from 'services/mailbox-api'
import settings from 'popups/Settings'
import dataMgr from 'pages/main/data/DataManager'
import SendAsEmailInput from './SendAsEmailInput'
import { useTranslation } from 'react-i18next'
import { Loading3QuartersOutlined } from '@ant-design/icons'

interface SendAsAccount {
  id: number | null
  name: string
  email: string
  isSelected: boolean
  isDefault: boolean
}

interface SendAsPanelProps {
  active: boolean
  onLoadAccounts: (accounts: SendAsAccount[]) => void
  onRemoveAccount: (account: SendAsAccount) => void
}

const SendAsPanel: React.FC<SendAsPanelProps> = ({ active, onLoadAccounts, onRemoveAccount }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [accounts, setAccounts] = useState<SendAsAccount[]>([])
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [domain, setDomain] = useState('')
  const [editSendAs, setEditSendAs] = useState<SendAsAccount | null>(null)
  const { t } = useTranslation()

  // 의도적으로 owner.onChangeValue를 거치지 않고 Redux에 직접 저장한다.
  // sendAsAccounts는 서버 API에서 가져오는 읽기 전용 목록이므로 Cancel 대상이 아니다.
  const assignAccounts = (accounts: SendAsAccount[]): void => {
    setAccounts(accounts)

    const settingsAccounts = accounts.filter((a) => !!a.id)
    settings.setValue('localSettings.email.sendAsAccounts', settingsAccounts)
  }

  useEffect(() => {
    if (!active) {
      setIsLoading(false)
      return
    }

    const userInfo = dataMgr.getUserInfo()
    const userDomain = userInfo.email.split('@')[1]
    setDomain(userDomain)
    const selectedAccount = settings.getValue('localSettings')?.email?.defaultSendAsAccount

    const mainAccount: SendAsAccount = {
      id: null,
      name: settings.getValue('account').accountSenderName,
      email: userInfo.email,
      isSelected: selectedAccount?.email === userInfo.email,
      isDefault: true,
    }
    setAccounts([mainAccount])

    mailboxApiHelper
      .getAliasesAndGroupMails(userDomain)
      .then(({ aliases, groupmails }: any) => {
        settings.setValue('localSettings.email.sendAsAccounts', aliases)
        settings.setValue('localSettings.email.groupMails', groupmails)

        const resultAlias = aliases.map((alias: any) => {
          return {
            ...alias,
            isSelected: selectedAccount?.email === alias.email,
            isDefault: false,
          }
        })
        setAccounts([mainAccount, ...resultAlias])
        onLoadAccounts([mainAccount, ...resultAlias])
      })
      .catch((err: Error) => {
        console.log(err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [active])

  const handleError = (type: string): void => {
    if (type === 'add') {
      AntMessage.info(t('settings.sendAs.error.add'))
    } else if (type === 'edit') {
      AntMessage.info(t('settings.sendAs.error.edit'))
    } else if (type === 'delete') {
      AntMessage.info(t('settings.sendAs.error.delete'))
    }
  }

  const handleAdd = (account: any): void => {
    console.log('add', account)
    setIsLoading(true)

    mailboxApiHelper
      .postAlias({
        name: account.name,
        local_part: account.email.split('@')[0],
        enabled: true,
      })
      .then((res: any) => {
        console.log('res', res.data)
        const alias = res.data
        const email = `${alias.local_part}@${domain}`
        const newAccount: SendAsAccount = {
          id: alias.id,
          name: alias.name,
          email,
          isSelected: false,
          isDefault: false,
        }
        const newAccounts = [...accounts, newAccount]
        assignAccounts(newAccounts)
        onLoadAccounts(newAccounts)
      })
      .catch((err: Error) => {
        console.log(err)
        handleError('add')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleEdit = (account: SendAsAccount): void => {
    console.log('edit', account)
    setEditSendAs(account)
    setShowEmailInput(true)
  }

  const handleSaveEdit = (data: any): void => {
    console.log('saveEdit', data)
    setIsLoading(true)

    mailboxApiHelper
      .putAlias(editSendAs!.id!.toString(), {
        name: data.name,
        local_part: data.email.split('@')[0],
        enabled: true,
      })
      .then((res: any) => {
        console.log('res', res.data)
        const alias = res.data
        const email = `${alias.local_part}@${domain}`
        const newAccount: SendAsAccount = {
          id: alias.id,
          name: alias.name,
          email,
          isSelected: false,
          isDefault: false,
        }
        const newAccounts = accounts.map((a) => {
          if (a.id === editSendAs!.id) {
            return newAccount
          }
          return a
        })
        assignAccounts(newAccounts)
        onLoadAccounts(newAccounts)
        setEditSendAs(null)
      })
      .catch((err: Error) => {
        console.log(err)
        handleError('edit')
      })
      .finally(() => {
        setIsLoading(false)
      })

    setShowEmailInput(false)
  }

  const handleDelete = (account: SendAsAccount): void => {
    if (account.id === null) {
      return
    }

    setIsLoading(true)

    mailboxApiHelper
      .deleteAlias(account.id!.toString())
      .then((res: any) => {
        console.log('res', res)
        const newAccounts = accounts.filter((a) => a.id !== account.id)
        assignAccounts(newAccounts)
        onLoadAccounts(newAccounts)
        onRemoveAccount(account)
      })
      .catch((err: Error) => {
        console.log(err)
        handleError('delete')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleMakeDefault = (account: SendAsAccount): void => {
    console.log('makeDefault', account)
    settings.setValue('localSettings.email.defaultSendAsAccount', {
      name: account.name,
      email: account.email,
    })

    const newAccounts = accounts.map((a) => {
      if (a.email === account.email) {
        a.isSelected = true
      } else {
        a.isSelected = false
      }
      return a
    })
    setAccounts(newAccounts)
  }

  const noneSelectedMenuItems = [
    {
      key: 'makeDefault',
      label: t('settings.accountTabs.makeDefault'),
    },
  ]

  const menuItems = [
    { key: 'edit', label: t('settings.accountTabs.edit') },
    { key: 'delete', label: t('settings.accountTabs.delete') },
  ]

  return (
    <div className={styles.sendAsPanel}>
      <h2 className={styles.title}>{t('settings.accountTabs.sendMailAs')}</h2>
      <Spin indicator={<Loading3QuartersOutlined spin />} spinning={isLoading}>
        <div className={styles.accountList}>
          {accounts.map((account) => (
            <div
              key={account.email}
              className={`${styles.accountItem} ${account.isSelected ? styles.selected : ''}`}
            >
              <div className={styles.checkIcon}>
                {account.isSelected && <img src={menuCheckGray16} alt="check" />}
              </div>
              <div className={styles.accountInfo}>
                <span className={styles.name} onWheel={(e) => e.stopPropagation()}>
                  <Tooltip title={account.name}>{account.name}</Tooltip>
                </span>
                <div style={{ width: 8 }} />
                <span className={styles.email} onWheel={(e) => e.stopPropagation()}>
                  <Tooltip title={account.email}>
                    <span>{`<${account.email}>`}</span>
                  </Tooltip>
                  {((account.isDefault && !account.isSelected) || !account.isDefault) && (
                    <Dropdown
                      overlayClassName="contextMenu"
                      trigger={['click']}
                      menu={{
                        items: account.isDefault
                          ? noneSelectedMenuItems
                          : account.isSelected
                            ? menuItems
                            : [...noneSelectedMenuItems, ...menuItems],
                        onWheel: (e) => e.stopPropagation(),
                        onClick: (e) => {
                          console.log('click', e)
                          if (e.key === 'makeDefault') {
                            handleMakeDefault(account)
                          } else if (e.key === 'edit') {
                            handleEdit(account)
                          } else if (e.key === 'delete') {
                            handleDelete(account)
                          }
                        },
                      }}
                    >
                      <div className={styles.moreButton}>
                        <img src={buttonMenu} alt="menu" />
                      </div>
                    </Dropdown>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.buttonContainer}>
          <button className={styles.addButton} onClick={() => setShowEmailInput(true)}>
            {t('settings.accountTabs.sendAsEditAdd')}
          </button>
        </div>
      </Spin>

      {showEmailInput && (
        <SendAsEmailInput
          editSendAs={editSendAs}
          domain={domain}
          onClose={() => setShowEmailInput(false)}
          onSave={(data) => {
            if (editSendAs) {
              handleSaveEdit(data)
            } else {
              handleAdd({
                ...data,
                email: `${data.email}@${domain}`,
              })
              setShowEmailInput(false)
            }
          }}
        />
      )}
    </div>
  )
}

export default SendAsPanel
